import React, { useRef, useEffect } from "react";
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { OrbitControls } from "three-stdlib";

const MilkCrateViewer = ({ tintColor }) => {
  const mountRef = useRef(null);
  const glassMaterialRef = useRef(null);
  const tintRef = useRef(tintColor);
  tintRef.current = tintColor;

  useEffect(() => {
    const mount = mountRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe3e1de); // Studio wall tone

    const camera = new THREE.PerspectiveCamera(
      45,
      mount.clientWidth / mount.clientHeight || 1,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    // Environment map for realistic glass reflections & refraction
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const hdrLoader = new RGBELoader();
    hdrLoader.load("/z/studio1.hdr", (texture) => {
      const envMap = pmremGenerator.fromEquirectangular(texture).texture;
      scene.environment = envMap;
      texture.dispose();
      pmremGenerator.dispose();
    });

    // Studio lighting: soft key light + gentle fill
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
    keyLight.position.set(6, 10, 6);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.near = 1;
    keyLight.shadow.camera.far = 40;
    keyLight.shadow.camera.left = -8;
    keyLight.shadow.camera.right = 8;
    keyLight.shadow.camera.top = 8;
    keyLight.shadow.camera.bottom = -8;
    keyLight.shadow.radius = 8;
    keyLight.shadow.blurSamples = 16;
    keyLight.shadow.bias = -0.0002;
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-6, 4, -4);
    scene.add(fillLight);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambientLight);

    // Studio floor: polished concrete look
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 400),
      new THREE.MeshStandardMaterial({
        color: 0x9fa0a2,
        roughness: 0.3,
        metalness: 0.15,
        envMapIntensity: 0.7,
      })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Soft shadow catcher layered just above the floor
    const shadowCatcher = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 400),
      new THREE.ShadowMaterial({ opacity: 0.35 })
    );
    shadowCatcher.rotation.x = -Math.PI / 2;
    shadowCatcher.position.y = 0.01;
    shadowCatcher.receiveShadow = true;
    scene.add(shadowCatcher);

    // Studio back wall
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 200),
      new THREE.MeshStandardMaterial({
        color: 0xe3e1de,
        roughness: 0.95,
        metalness: 0,
      })
    );
    wall.position.set(0, 100, -40);
    scene.add(wall);

    // Adding OrbitControls for interactivity
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2 - 0.02; // Stay above the floor

    // Load the model
    const loader = new OBJLoader();
    loader.load(
      "/z/PlasticCrate3.obj",
      (object) => {
        object.scale.set(0.1, 0.1, 0.1);

        // Center the crate and rest it on the floor
        const box = new THREE.Box3().setFromObject(object);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        object.position.x -= center.x;
        object.position.z -= center.z;
        object.position.y -= box.min.y;
        const maxDim = Math.max(size.x, size.y, size.z);

        // Realistic clear glass material
        const glassMaterial = new THREE.MeshPhysicalMaterial({
          color: tintRef.current,
          metalness: 0,
          roughness: 0.03,
          transmission: 1, // Fully transmissive like real glass
          thickness: maxDim * 0.12, // Refraction depth through the ribs
          ior: 1.5, // Index of refraction of glass
          specularIntensity: 1,
          clearcoat: 1,
          clearcoatRoughness: 0.06,
          envMapIntensity: 1.8,
          side: THREE.DoubleSide, // Back faces add depth to the ribs

          attenuationColor: new THREE.Color(tintRef.current),
          attenuationDistance: maxDim * 2,
        });
        glassMaterialRef.current = glassMaterial;

        object.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.material = glassMaterial;
            child.castShadow = true;
          }
        });

        scene.add(object);

        // Scale the lighting rig and shadow frustum to the crate
        keyLight.position.set(maxDim, maxDim * 1.6, maxDim);
        keyLight.shadow.camera.far = maxDim * 6;
        keyLight.shadow.camera.left = -maxDim * 1.5;
        keyLight.shadow.camera.right = maxDim * 1.5;
        keyLight.shadow.camera.top = maxDim * 1.5;
        keyLight.shadow.camera.bottom = -maxDim * 1.5;
        keyLight.shadow.camera.updateProjectionMatrix();

        // Frame the crate like a product shot
        camera.position.set(maxDim * 1.5, maxDim * 1.05, maxDim * 1.9);
        controls.target.set(0, size.y * 0.45, 0);
        controls.minDistance = maxDim * 0.8;
        controls.maxDistance = maxDim * 6;
        controls.update();
      },
      undefined,
      (error) => {
        console.log("An error happened", error);
      }
    );

    // Fallback camera position until the model loads
    camera.position.set(0, 5, 15);

    // Handle window resize
    const handleResize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", handleResize);

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      controls.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Retint the glass without rebuilding the scene or reloading the model
  useEffect(() => {
    const material = glassMaterialRef.current;
    if (material) {
      material.color.set(tintColor);
      material.attenuationColor.set(tintColor);
    }
  }, [tintColor]);

  return <div ref={mountRef} className="my3DViewer" />;
};

export default MilkCrateViewer;
